import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Trash2, Plus, Copy, ArrowUp, ArrowDown } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import type {
  FieldType,
  FormAnswerValue,
  FormField,
  FormStep,
  OnboardingFormConfig,
} from '@/types';

export type { FieldType, FormAnswerValue, FormField, FormStep, OnboardingFormConfig };

interface FormBuilderProps {
  initialConfig?: OnboardingFormConfig;
  onChange: (config: OnboardingFormConfig) => void;
  /** Requires approval enabled — lets you defer some questions until after admins accept joins. */
  showBeforeApprovalToggle?: boolean;
}

export function FormBuilder({
  initialConfig,
  onChange,
  showBeforeApprovalToggle = false,
}: FormBuilderProps) {
  const [steps, setSteps] = useState<FormStep[]>(() => {
    const rawSteps = initialConfig?.steps;
    if (Array.isArray(rawSteps) && rawSteps.length > 0) {
      // Ensure each step has a fields array
      return rawSteps.map((step) => ({
        ...step,
        fields: Array.isArray(step.fields) ? step.fields : [],
      }));
    }
    return [{ id: crypto.randomUUID(), title: 'Tell us about yourself', fields: [] }];
  });

  const updateSteps = (newSteps: FormStep[]) => {
    const sanitizedSteps = newSteps.map((s) => ({
      ...s,
      fields: Array.isArray(s.fields) ? s.fields : [],
    }));
    setSteps(sanitizedSteps);
    onChange({ steps: sanitizedSteps });
  };

  const addField = (stepIndex: number) => {
    const newSteps = [...steps];
    newSteps[stepIndex].fields.push({
      id: crypto.randomUUID(),
      type: 'TEXT',
      label: 'New Question',
      required: true,
      options: [],
      cropAspect: 1,
      enableCrop: true,
      showForPending: true,
    });
    updateSteps(newSteps);
  };

  const updateField = (stepIndex: number, fieldIndex: number, updates: Partial<FormField>) => {
    const newSteps = [...steps];
    newSteps[stepIndex].fields[fieldIndex] = {
      ...newSteps[stepIndex].fields[fieldIndex],
      ...updates,
    };
    updateSteps(newSteps);
  };

  const removeField = (stepIndex: number, fieldIndex: number) => {
    const newSteps = [...steps];
    newSteps[stepIndex].fields.splice(fieldIndex, 1);
    updateSteps(newSteps);
  };

  const duplicateField = (stepIndex: number, fieldIndex: number) => {
    const newSteps = [...steps];
    const fieldToDuplicate = newSteps[stepIndex].fields[fieldIndex];
    const duplicatedField = {
      ...fieldToDuplicate,
      id: crypto.randomUUID(), // Generate new ID for the duplicate
    };
    newSteps[stepIndex].fields.splice(fieldIndex + 1, 0, duplicatedField);
    updateSteps(newSteps);
  };

  const moveFieldUp = (stepIndex: number, fieldIndex: number) => {
    if (fieldIndex === 0) return; // Already at the top
    const newSteps = [...steps];
    const [field] = newSteps[stepIndex].fields.splice(fieldIndex, 1);
    newSteps[stepIndex].fields.splice(fieldIndex - 1, 0, field);
    updateSteps(newSteps);
  };

  const moveFieldDown = (stepIndex: number, fieldIndex: number) => {
    const newSteps = [...steps];
    if (fieldIndex === newSteps[stepIndex].fields.length - 1) return; // Already at the bottom
    const [field] = newSteps[stepIndex].fields.splice(fieldIndex, 1);
    newSteps[stepIndex].fields.splice(fieldIndex + 1, 0, field);
    updateSteps(newSteps);
  };

  const addOption = (stepIndex: number, fieldIndex: number) => {
    const newSteps = [...steps];
    const field = newSteps[stepIndex].fields[fieldIndex];
    if (!field.options) field.options = [];
    field.options.push('');
    updateSteps(newSteps);
  };

  const updateOption = (
    stepIndex: number,
    fieldIndex: number,
    optionIndex: number,
    value: string
  ) => {
    const newSteps = [...steps];
    const field = newSteps[stepIndex].fields[fieldIndex];
    if (field.options) {
      field.options[optionIndex] = value;
      updateSteps(newSteps);
    }
  };

  const removeOption = (stepIndex: number, fieldIndex: number, optionIndex: number) => {
    const newSteps = [...steps];
    const field = newSteps[stepIndex].fields[fieldIndex];
    if (field.options) {
      field.options.splice(optionIndex, 1);
      updateSteps(newSteps);
    }
  };

  const handlePasteOption = (
    stepIndex: number,
    fieldIndex: number,
    optionIndex: number,
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = e.clipboardData.getData('text');
    if (text.includes('\n') || text.includes('\r')) {
      e.preventDefault();
      const pastedLines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      if (pastedLines.length > 0) {
        const newSteps = [...steps];
        const field = newSteps[stepIndex].fields[fieldIndex];
        if (field.options) {
          const currentOptions = [...field.options];
          currentOptions[optionIndex] = pastedLines[0];
          currentOptions.splice(optionIndex + 1, 0, ...pastedLines.slice(1));
          field.options = currentOptions;
          updateSteps(newSteps);
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      {steps.filter(Boolean).map((step, stepIndex) => (
        <Card
          key={step.id}
          className="border-2 border-dashed border-border/60 hover:border-border/80 transition-colors"
        >
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1 w-full max-w-sm">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
                  Step Title
                </Label>
                <Input
                  value={step.title}
                  onChange={(e) => {
                    const newSteps = [...steps];
                    newSteps[stepIndex].title = e.target.value;
                    updateSteps(newSteps);
                  }}
                  className="font-bold text-lg border-transparent focus-visible:border-primary hover:bg-accent/10 focus-visible:bg-transparent -ml-3 px-3 transition-colors"
                />
              </div>
              <Badge variant="secondary" className="text-xs font-mono">
                Step {stepIndex + 1}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {step.fields.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground/60 text-sm italic">
                No questions yet. Add one below.
              </div>
            ) : (
              <div className="space-y-4">
                {step.fields.map((field, fieldIndex) => {
                  const showWhilePending = field.showForPending !== false;
                  return (
                    <motion.div
                      key={field.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-accent/5 rounded-xl border border-border p-4 space-y-4 group relative"
                    >
                      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 hover:bg-accent"
                          onClick={() => moveFieldUp(stepIndex, fieldIndex)}
                          disabled={fieldIndex === 0}
                          title="Move up"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 hover:bg-accent"
                          onClick={() => moveFieldDown(stepIndex, fieldIndex)}
                          disabled={fieldIndex === step.fields.length - 1}
                          title="Move down"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-primary hover:bg-primary/10"
                          onClick={() => duplicateField(stepIndex, fieldIndex)}
                          title="Duplicate field"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          onClick={() => removeField(stepIndex, fieldIndex)}
                          title="Delete field"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="grid gap-4 md:grid-cols-[2fr,1fr]">
                        <div className="space-y-2">
                          <Label>Question</Label>
                          <Input
                            value={field.label}
                            onChange={(e) =>
                              updateField(stepIndex, fieldIndex, { label: e.target.value })
                            }
                            className="font-medium"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select
                            value={field.type}
                            onValueChange={(value: FieldType) => {
                              if (value === 'IMAGE') {
                                updateField(stepIndex, fieldIndex, {
                                  type: value,
                                  cropAspect: field.cropAspect ?? 1,
                                  enableCrop: field.enableCrop ?? true,
                                });
                              } else {
                                updateField(stepIndex, fieldIndex, { type: value });
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="TEXT">Text Input</SelectItem>
                              <SelectItem value="TEXTAREA">Long Text</SelectItem>
                              <SelectItem value="NUMBER">Number</SelectItem>
                              <SelectItem value="SELECT">Single Select</SelectItem>
                              <SelectItem value="MULTI_SELECT">Multi Select</SelectItem>
                              <SelectItem value="IMAGE">Image</SelectItem>
                              <SelectItem value="DATE">Date Picker</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={field.required}
                            onCheckedChange={(checked) =>
                              updateField(stepIndex, fieldIndex, { required: checked })
                            }
                            id={`req-${field.id}`}
                          />
                          <Label
                            htmlFor={`req-${field.id}`}
                            className="text-sm text-muted-foreground font-medium cursor-pointer"
                          >
                            Required
                          </Label>
                        </div>
                        {showBeforeApprovalToggle ? (
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={field.showForPending !== false}
                              onCheckedChange={(checked) =>
                                updateField(stepIndex, fieldIndex, { showForPending: checked })
                              }
                              id={`pending-${field.id}`}
                            />
                            <div className="space-y-0.5">
                              <Label
                                htmlFor={`pending-${field.id}`}
                                className="text-sm text-muted-foreground font-medium cursor-pointer"
                              >
                                {showWhilePending
                                  ? 'Ask while request is pending'
                                  : 'Ask only after acceptance'}
                              </Label>
                              <p className="text-xs text-muted-foreground/90 leading-snug max-w-[220px]">
                                {showWhilePending
                                  ? 'Users see this when they request to join.'
                                  : 'Only members whose request is approved will see this question.'}
                              </p>
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {field.type === 'IMAGE' && (
                        <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={field.enableCrop !== false}
                                onCheckedChange={(checked) =>
                                  updateField(stepIndex, fieldIndex, { enableCrop: checked })
                                }
                                id={`enable-crop-${field.id}`}
                              />
                              <Label
                                htmlFor={`enable-crop-${field.id}`}
                                className="text-sm text-muted-foreground font-medium cursor-pointer"
                              >
                                Enable cropping
                              </Label>
                            </div>
                            <p className="text-xs text-muted-foreground pl-9">
                              When off, members upload the image as-is with no crop step.
                            </p>
                          </div>

                          {field.enableCrop !== false ? (
                            <div className="space-y-2">
                              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                                Crop aspect ratio
                              </Label>
                              <Select
                                value={
                                  field.cropAspect === 1
                                    ? '1:1'
                                    : field.cropAspect === 4 / 3
                                      ? '4:3'
                                      : field.cropAspect === 16 / 9
                                        ? '16:9'
                                        : field.cropAspect === 3 / 2
                                          ? '3:2'
                                          : field.cropAspect &&
                                              ![1, 4 / 3, 16 / 9, 3 / 2].includes(field.cropAspect)
                                            ? 'custom'
                                            : '1:1'
                                }
                                onValueChange={(value) => {
                                  const aspectMap: Record<string, number> = {
                                    '1:1': 1,
                                    '4:3': 4 / 3,
                                    '16:9': 16 / 9,
                                    '3:2': 3 / 2,
                                    custom: 1.51, // Small offset to trigger custom view and not match any preset
                                  };
                                  updateField(stepIndex, fieldIndex, {
                                    cropAspect: aspectMap[value],
                                  });
                                }}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1:1">1:1 (Square)</SelectItem>
                                  <SelectItem value="4:3">4:3 (Standard)</SelectItem>
                                  <SelectItem value="16:9">16:9 (Widescreen)</SelectItem>
                                  <SelectItem value="3:2">3:2 (Photo)</SelectItem>
                                  <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                              </Select>
                              {field.cropAspect !== undefined &&
                                ![1, 4 / 3, 16 / 9, 3 / 2].includes(field.cropAspect) && (
                                  <div className="flex gap-2 items-center">
                                    <Input
                                      type="number"
                                      placeholder="Width"
                                      className="h-8 text-sm"
                                      min="1"
                                      value={Math.round((field.cropAspect || 1) * 100) / 100}
                                      onChange={(e) => {
                                        const width = parseFloat(e.target.value) || 1;
                                        updateField(stepIndex, fieldIndex, { cropAspect: width });
                                      }}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                      Aspect Ratio
                                    </span>
                                  </div>
                                )}
                            </div>
                          ) : null}
                        </div>
                      )}

                      {(field.type === 'SELECT' || field.type === 'MULTI_SELECT') && (
                        <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                          <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                            Options
                          </Label>
                          <div className="space-y-2">
                            {field.options?.map((option, optIndex) => (
                              <div key={optIndex} className="flex gap-2">
                                <Input
                                  value={option}
                                  placeholder="Enter option value (or paste multi-line list)"
                                  onChange={(e) =>
                                    updateOption(stepIndex, fieldIndex, optIndex, e.target.value)
                                  }
                                  onPaste={(e) =>
                                    handlePasteOption(stepIndex, fieldIndex, optIndex, e)
                                  }
                                  className="h-8 text-sm"
                                />
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                  onClick={() => removeOption(stepIndex, fieldIndex, optIndex)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => addOption(stepIndex, fieldIndex)}
                              className="text-primary hover:text-primary/80 h-8 px-2"
                            >
                              <Plus className="h-3 w-3 mr-1" /> Add Option
                            </Button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}

            <Button
              variant="outline"
              className="w-full border-dashed border-2 py-6 text-muted-foreground hover:text-primary hover:border-primary/50"
              onClick={() => addField(stepIndex)}
            >
              <Plus className="mr-2 h-4 w-4" /> Add Question
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
